import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { rawFetch } from "@/lib/api/client";
import { ErrorBox } from "@/components/common";
import { Button } from "@/components/ui-kit";
import { CloudDictionaryBrowser } from "@/components/CloudDictionaryBrowser";
import { DictionaryImporter, type ImportReceipt } from "@/components/DictionaryImporter";

type Proposal = {
  id: string; status: string; revision: number;
  normalized_claim: { name: string; definition: string; data_entity?: string; review_flags?: string[] };
};
type Saved = { id: string; latest_version_id: string; versions: { entry_count: number }[] };

export function TermSetImportFlow({ workspaceId, canAdmin, onSaved }: {
  workspaceId: string; canAdmin: boolean; onSaved: (saved: Saved) => void;
}) {
  const qc = useQueryClient();
  const [source, setSource] = useState<"upload" | "s3">("upload");
  const [step, setStep] = useState<0 | 1 | 2>(0);
  const [receipt, setReceipt] = useState<ImportReceipt | null>(null);
  const [entity, setEntity] = useState("");
  const [filter, setFilter] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState<Error | null>(null);
  const rows = useQuery<Proposal[]>({
    queryKey: ["term-import-review", workspaceId, receipt?.runId],
    enabled: Boolean(workspaceId && receipt),
    queryFn: async () => {
      const results: Proposal[] = [];
      let count = 0;
      do {
        const page = await rawFetch<{ count: number; results: Proposal[] }>(
          `/catalog/discovery/proposals/?workspace=${encodeURIComponent(workspaceId)}&run_id=${encodeURIComponent(receipt!.runId)}&limit=100&offset=${results.length}`,
        );
        count = page.count;
        results.push(...page.results);
        if (!page.results.length) break;
      } while (results.length < count && results.length < 10_000);
      return results;
    },
  });
  const proposals = rows.data ?? [];
  const entities = useMemo(() => [...new Set(proposals.map((row) => row.normalized_claim.data_entity || "Unclassified"))].sort(), [proposals]);
  const visible = proposals.filter((row) => (!entity || (row.normalized_claim.data_entity || "Unclassified") === entity)
    && (!filter || row.normalized_claim.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase())));
  const pending = proposals.filter((row) => row.status === "proposed");
  const approved = proposals.filter((row) => row.status === "approved");
  const conflicts = proposals.filter((row) => row.status === "source_conflict");
  const rejected = proposals.filter((row) => row.status === "rejected");
  const sample = visible.filter((_, index) => index % Math.max(1, Math.floor(visible.length / 8)) === 0).slice(0, 8);

  function staged(value: ImportReceipt) {
    setReceipt(value); setStep(1); setError(null); setMessage("");
    void qc.invalidateQueries({ queryKey: ["term-import-review", workspaceId] });
  }

  async function approveBatch(batch: Proposal[]) {
    for (let index = 0; index < batch.length; index += 500) {
      const part = batch.slice(index, index + 500);
      await rawFetch("/catalog/discovery/proposals/bulk/", { method: "POST", body: JSON.stringify({
        workspace: workspaceId, action: "approve", proposal_ids: part.map((row) => row.id),
        expected_revisions: Object.fromEntries(part.map((row) => [row.id, row.revision])),
        reason: "evidence_verified", request_id: crypto.randomUUID(),
      }) });
    }
    await rows.refetch();
  }

  async function act(task: () => Promise<void>) {
    setBusy(true); setError(null); setMessage("");
    try { await task(); } catch (caught) { setError(caught as Error); }
    finally { setBusy(false); }
  }

  async function save() {
    if (!approved.length || !name.trim()) return;
    await act(async () => {
      const saved = await rawFetch<Saved>("/ontology/term-sets/", { method: "POST", body: JSON.stringify({
        workspace: workspaceId, name: name.trim(), proposal_ids: approved.map((row) => row.id), tags: {},
      }) });
      onSaved(saved); setStep(2);
      setMessage(`${saved.versions[0]?.entry_count ?? approved.length} approved terms saved as an immutable version.`);
    });
  }

  async function approveAndSave() {
    if (!name.trim() || !proposals.length || proposals.length > 500 || conflicts.length || rejected.length) return;
    await act(async () => {
      const saved = await rawFetch<Saved>("/ontology/term-sets/approve-and-save/", {
        method: "POST", body: JSON.stringify({
          workspace: workspaceId, name: name.trim(), tags: {},
          proposal_ids: proposals.map((row) => row.id),
          expected_revisions: Object.fromEntries(proposals.map((row) => [row.id, row.revision])),
          request_id: crypto.randomUUID(),
        }),
      });
      onSaved(saved); setStep(2);
      setMessage(`${saved.versions[0]?.entry_count ?? proposals.length} terms approved and saved. The shortcut was audited.`);
      await rows.refetch();
    });
  }

  return <section className="space-y-3" aria-label="Guided term import">
    <ol className="flex flex-wrap gap-2 text-xs" aria-label="Import progress">
      {(["Import", "Review", "Save as term set"] as const).map((label, index) =>
        <li key={label} className={`border px-3 py-2 ${step === index ? "border-accent text-accent" : "border-border text-muted"}`}>{index + 1}. {label}</li>)}
    </ol>
    {step === 0 && <>
      <div className="flex gap-2" role="group" aria-label="Dictionary source">
        <Button variant={source === "upload" ? "primary" : "secondary"} onClick={() => setSource("upload")}>Upload</Button>
        {canAdmin && <Button variant={source === "s3" ? "primary" : "secondary"} onClick={() => setSource("s3")}>S3</Button>}
      </div>
      {source === "upload" ? <DictionaryImporter workspaceId={workspaceId} onStaged={staged} />
        : <CloudDictionaryBrowser workspaceId={workspaceId} onStaged={staged} />}
    </>}
    {step >= 1 && receipt && <>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
        <p role="status">{receipt.proposalCount} found · {approved.length} approved · {pending.length} pending · {conflicts.length} conflicts · {rejected.length} rejected</p>
        <Button variant="secondary" onClick={() => setStep(0)}>Import another file</Button>
      </div>
      {rows.isError && <ErrorBox error={rows.error} />}
      {rows.isLoading && <p className="text-xs text-muted">Loading review queue…</p>}
      {step === 1 && <>
        <div className="grid gap-2 sm:grid-cols-2">
          <label className="text-xs">Data entity<select className="input mt-1" value={entity} onChange={(event) => setEntity(event.target.value)}><option value="">All entities</option>{entities.map((value) => <option key={value}>{value}</option>)}</select></label>
          <label className="text-xs">Filter terms<input className="input mt-1" value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="Find a term" /></label>
        </div>
        <p className="text-xs text-muted">Spot-check sample ({sample.length} of {visible.length} visible). Inspect definitions and possible-name/address flags before a bulk decision.</p>
        <div className="max-h-64 overflow-auto border border-border text-xs"><table className="w-full"><thead><tr><th className="p-2 text-left">Term</th><th className="p-2 text-left">Entity</th><th className="p-2 text-left">Definition</th><th className="p-2 text-left">Status</th></tr></thead><tbody>
          {sample.map((row) => <tr key={row.id} className="border-t border-border"><td className="p-2">{row.normalized_claim.name}</td><td className="p-2">{row.normalized_claim.data_entity || "Unclassified"}</td><td className="p-2">{row.normalized_claim.definition}{row.normalized_claim.review_flags?.length ? <span className="block text-amber-600">Review possible name or address</span> : null}</td><td className="p-2">{row.status}</td></tr>)}
        </tbody></table></div>
        {canAdmin && <div className="flex flex-wrap gap-2">
          <Button disabled={busy || !visible.some((row) => row.status === "proposed")} onClick={() => void act(async () => {
            const batch = visible.filter((row) => row.status === "proposed");
            await approveBatch(batch); setMessage(`${batch.length} visible terms approved.`);
          })}>Approve {visible.filter((row) => row.status === "proposed").length} {entity || filter ? "filtered" : "pending"} terms</Button>
          {entity && <span className="self-center text-xs text-muted">Entity: {entity}</span>}
        </div>}
        {conflicts.length > 0 && <p role="alert" className="text-xs text-amber-600">Resolve {conflicts.length} conflicting terms individually in <Link to="/catalog" className="underline">Catalog review</Link> before approving them.</p>}
        {!canAdmin && <p className="text-xs text-muted">An admin or owner approves proposals. You can inspect and edit individual proposals in <Link to="/catalog" className="underline">Catalog review</Link>.</p>}
        <label className="block text-xs">Term-set name<input className="input mt-1" value={name} maxLength={160} onChange={(event) => setName(event.target.value)} placeholder="e.g. Motor vocabulary" /></label>
        <div className="flex flex-wrap gap-2">
          <Button variant="primary" disabled={busy || !approved.length || !name.trim()} onClick={() => void save()}>Save {approved.length} approved terms as term set</Button>
          {canAdmin && <Button disabled={busy || !name.trim() || !proposals.length || proposals.length > 500 || conflicts.length > 0 || rejected.length > 0} onClick={() => void approveAndSave()}>Approve all and save as term set</Button>}
        </div>
        {proposals.length > 500 && <p className="text-xs text-muted">The audited shortcut handles up to 500 terms at once. Approve by entity or filter, then save the approved set.</p>}
      </>}
      {step === 2 && <p className="text-xs text-good">The approved term set is ready to create an ontology draft.</p>}
    </>}
    {message && <p role="status" className="text-xs text-good">{message}</p>}
    {error && <ErrorBox error={error} />}
  </section>;
}
